import { useState, type FormEvent } from "react";
import { Sun, Mail, MailCheck, ArrowRight, Loader2 } from "lucide-react";
import { signInWithEmail } from "../lib/session.ts";

export function Login(props: { onSignedIn: (email: string) => void; authEnabled: boolean }) {
  const { onSignedIn, authEnabled } = props;

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-b from-amber-50 via-slate-50 to-slate-50 px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="card p-8">
          <div className="flex flex-col items-center text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-sm">
              <Sun size={28} />
            </span>
            <h1 className="mt-4 text-2xl font-bold text-slate-900">Solar R&amp;R</h1>
            <p className="mt-1 text-sm text-slate-500">Operator sign-in</p>
          </div>

          <div className="mt-8">
            {authEnabled ? (
              sent ? (
                <div className="flex flex-col items-center text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                    <MailCheck size={24} />
                  </span>
                  <h2 className="mt-3 text-base font-semibold text-slate-900">Check your email</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    We sent a magic link to{" "}
                    <span className="font-medium text-slate-700">{email.trim()}</span>. Click it to
                    finish signing in.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSent(false);
                      setError(null);
                    }}
                    className="btn-ghost mt-4"
                  >
                    Use a different email
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="label">
                      Email address
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="operator@example.com"
                      className="input"
                      disabled={submitting}
                    />
                  </div>

                  {error && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
                  )}

                  <button type="submit" className="btn-primary w-full" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending&hellip;
                      </>
                    ) : (
                      <>
                        <Mail size={16} />
                        Send magic link
                      </>
                    )}
                  </button>
                </form>
              )
            ) : (
              <div className="flex flex-col items-center text-center">
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Authentication is disabled in local dev. You&rsquo;ll be signed in as a sample
                  operator.
                </p>
                <button
                  type="button"
                  onClick={() => onSignedIn("operator@example.com")}
                  className="btn-primary mt-4 w-full"
                >
                  Continue
                  <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">Solar R&amp;R Job Automation</p>
      </div>
    </div>
  );
}
